import React, { memo, useCallback, useMemo } from 'react'

import { FiltersSidebar } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import formatResultsCount from 'Pods/SearchPage/Shared/formatResultsCount'
import { FILTER_NAME } from 'Pods/SearchPage/Shared/Tracking/constants'
import { useTrackFilterSelect } from 'Pods/SearchPage/Shared/Tracking/FilterTracking'
import useTranslate, { useLocale } from 'Pods/SearchPage/Shared/useTranslate'
import { toggleSubscriptionTier } from 'Pods/SearchPage/Store/actions/pricing'
import { SUBSCRIPTION_TIER } from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

const PLAN_NAMES = {
  [SUBSCRIPTION_TIER.ESSENTIALS]: 'Essentials',
  [SUBSCRIPTION_TIER.PREMIUM]: 'Premium',
}

function SubscriptionTier() {
  const translate = useTranslate()
  const locale = useLocale()
  const subscriberTier = usePageConfig()?.subscriptionTier

  const withSubscription = useSearchPageStore(state => state.withSubscription)
  const subscriptionTiers = useSearchPageStore(state => state.subscriptionTiers)
  const tierCounts = useSearchPageStore(state => state.filterCounts.subscription_tier) || EMPTY_ARRAY
  const trackFilterSelect = useTrackFilterSelect()

  // A subscription search with no plan picked (an old "All" URL) spans every tier, which is what
  // Premium shows.
  const selectedFilters = useMemo(() => {
    if (!withSubscription) return EMPTY_ARRAY
    if (!subscriptionTiers?.length) return [SUBSCRIPTION_TIER.PREMIUM]

    return subscriptionTiers
  }, [withSubscription, subscriptionTiers])

  // Essentials/Premium are plan names, so they stay untranslated - same as the legacy listing.
  const filters = useMemo(() => {
    const planLabel = (tier, name) => {
      if (tier !== subscriberTier) return name

      return (
        <span className="price-filter__plan-label">
          {name}
          {' '}
          <span className="price-filter__your-plan cgt-overline-sm">
            {translate('search_page.filters.price.your_plan')}
          </span>
        </span>
      )
    }

    const countsByTier = new Map(tierCounts.map(({ key, count }) => [key, count]))
    const planCount = (tier) => {
      if (!countsByTier.has(tier)) return null

      const count = countsByTier.get(tier)
      const label = translate('search_page.results.models_count', { count, total: formatResultsCount(count, locale) })

      // FiltersGroup types `count` as a number or a node, so the label goes in as a node.
      return <span>{label}</span>
    }

    return [{
      label: translate('search_page.filters.price.with_subscription'),
      filters: [SUBSCRIPTION_TIER.ESSENTIALS, SUBSCRIPTION_TIER.PREMIUM].map(tier => ({
        value: tier,
        label: planLabel(tier, PLAN_NAMES[tier]),
        count: planCount(tier),
      })),
    }]
  }, [translate, locale, subscriberTier, tierCounts])

  const handleFilterChange = useCallback((_, value, isOn) => {
    if (isOn) {
      trackFilterSelect(FILTER_NAME.PRICE, `subscription_tier_${value}`)
    }
    toggleSubscriptionTier(value, isOn)
  }, [trackFilterSelect])

  return (
    <div className="price-filter__subscription-tier">
      <FiltersSidebar
        isCollapsible={false}
        filters={filters}
        selectedFilters={selectedFilters}
        onFiltersChange={handleFilterChange}
        limit={0}
        isControlled
        useNewCheckboxes
        hideLastDivider
        hideShowMore
        baseFormWrapperClassName="filter-wrapper__content"
      />
    </div>
  )
}

export default memo(SubscriptionTier)
